import { useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Copy, Download, Key, LockKeyhole, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { apiError, useCRMAuth } from '../context/CRMAuthContext';
import { TenantBrand } from '../../branding/BrandingContext';
import '../components/mfa.css';

export default function CRMLogin({ destination = '/admin', title = 'CRM Girişi' }) {
  const { user, login, expired } = useCRMAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const target = typeof from === 'string' && (/^\/crm(?:\/|$)/.test(from) || /^\/admin\/manager(?:\/|$)/.test(from)) && !from.endsWith('/login') ? from : destination;

  // step: 'credentials' | 'mfa' | 'setup' | 'recovery'
  const [step, setStep] = useState('credentials');
  const [values, setValues] = useState({ identifier: '', password: '', code: '' });
  const [setupData, setSetupData] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [savedRecovery, setSavedRecovery] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (user && !busy && step !== 'recovery') return <Navigate to={target} replace />;

  const submit = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const payload = {
        identifier: values.identifier,
        password: values.password,
        code: values.code ? values.code.trim() : undefined,
      };
      const result = await login(payload);

      if (result?.mfa_required) {
        setStep('mfa');
        setValues(v => ({ ...v, code: '' }));
      } else if (result?.mfa_setup_required) {
        setSetupData(result);
        setStep('setup');
        setValues(v => ({ ...v, code: '' }));
      } else if (result?.recovery_codes?.length) {
        setRecoveryCodes(result.recovery_codes);
        setStep('recovery');
      } else {
        navigate(target, { replace: true });
      }
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const downloadCodes = () => {
    const text = `WEBTRA CRM Acil Durum Kurtarma Kodlari\nKullanici: ${values.identifier}\nTarih: ${new Date().toISOString()}\n\n${recoveryCodes.join('\n')}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: 'crm-recovery-codes.txt' });
    a.click();
    URL.revokeObjectURL(url);
    setSavedRecovery(true);
  };

  const restart = () => {
    setStep('credentials');
    setValues({ identifier: '', password: '', code: '' });
    setSetupData(null);
    setError('');
  };

  return (
    <div className="crm-login" data-testid="crm-login-page">
      <div className="crm-login-photo" />
      <div className="crm-login-top">
        <span className="crm-wordmark">
          <TenantBrand id="crm-login-brand" fallback={<>BROKER<span>CRM</span></>} />
        </span>
        <span className="crm-login-label">KURUMSAL ERİŞİM</span>
      </div>

      <main className="crm-login-panel">
        <div className="crm-login-symbol">
          {step === 'credentials' ? <LockKeyhole size={24} /> : <ShieldCheck size={24} />}
        </div>

        <p className="crm-eyebrow">
          {step === 'credentials' && 'PERSONEL PORTALI'}
          {step === 'mfa' && 'İKİ AŞAMALI DOĞRULAMA'}
          {step === 'setup' && 'İLK BAĞLANTI (2FA KURULUMU)'}
          {step === 'recovery' && 'KURTARMA KODLARI'}
        </p>

        <h1>
          {step === 'credentials' && title}
          {step === 'mfa' && 'Doğrulama Kodu'}
          {step === 'setup' && 'Uygulamayı Bağla'}
          {step === 'recovery' && 'Kodları Saklayın'}
        </h1>

        <div className="crm-heading-line" />

        {/* STEP 1: USERNAME + PASSWORD ONLY */}
        {step === 'credentials' && (
          <form onSubmit={submit} data-testid="crm-login-form">
            <label htmlFor="crm-identifier">E-posta / Kullanıcı Adı</label>
            <Input
              id="crm-identifier"
              data-testid="crm-login-identifier"
              autoComplete="username"
              required
              autoFocus
              value={values.identifier}
              onChange={e => setValues({ ...values, identifier: e.target.value })}
            />

            <label htmlFor="crm-password">Şifre</label>
            <Input
              id="crm-password"
              data-testid="crm-login-password"
              type="password"
              autoComplete="current-password"
              required
              value={values.password}
              onChange={e => setValues({ ...values, password: e.target.value })}
            />

            {(error || expired) && (
              <p role="alert" className="crm-error" data-testid="crm-login-error">
                {error || 'Oturumunuz sona erdi.'}
              </p>
            )}

            <Button
              className="crm-primary crm-login-submit"
              type="submit"
              disabled={busy}
              data-testid="crm-login-submit"
            >
              {busy ? 'Kontrol ediliyor…' : 'Giriş Yap'}
              <ArrowRight size={17} />
            </Button>
          </form>
        )}

        {/* STEP 2A: 2FA CODE (IF ALREADY ENROLLED) */}
        {step === 'mfa' && (
          <form onSubmit={submit} data-testid="crm-mfa-step-form">
            <p className="crm-mfa-hint">
              Google Authenticator, Microsoft Authenticator veya Authy uygulamanızdaki 6 haneli kodu (veya kurtarma kodunuzu) girin:
            </p>

            <label htmlFor="crm-code">Doğrulama Kodu</label>
            <Input
              id="crm-code"
              data-testid="crm-login-code"
              autoComplete="one-time-code"
              maxLength={17}
              autoFocus
              placeholder="123456"
              value={values.code}
              onChange={e => setValues({ ...values, code: e.target.value.replace(/[^0-9a-fA-F-]/g, '').toLowerCase() })}
            />

            {error && (
              <p role="alert" className="crm-error" data-testid="crm-login-error">
                {error}
              </p>
            )}

            <Button
              className="crm-primary crm-login-submit"
              type="submit"
              disabled={busy || !values.code}
              data-testid="crm-login-verify"
            >
              {busy ? 'Doğrulanıyor…' : 'Doğrula ve Giriş Yap'}
              <ArrowRight size={17} />
            </Button>

            <button
              type="button"
              className="crm-mfa-logout"
              onClick={restart}
              style={{ marginTop: '16px' }}
            >
              <ArrowLeft size={14} /> Farklı kullanıcı ile giriş yap
            </button>
          </form>
        )}

        {/* STEP 2B: FIRST-TIME 2FA CONNECTION (QR CODE) */}
        {step === 'setup' && setupData && (
          <form onSubmit={submit} className="crm-mfa-enroll" data-testid="crm-setup-step-form">
            <p className="crm-mfa-hint">
              Hesabınızda iki aşamalı doğrulama henüz etkin değil. Lütfen aşağıdaki QR kodu <strong>Google Authenticator</strong> veya <strong>Authy</strong> ile tarayın:
            </p>

            {setupData.qr && (
              <img
                className="crm-mfa-qr"
                src={setupData.qr}
                alt="2FA QR kodu"
                data-testid="crm-login-qr"
              />
            )}

            <details className="crm-mfa-manual">
              <summary>
                <Key size={13} /> QR okutamıyor musunuz? Anahtarı elle girin
              </summary>
              <code className="crm-mfa-secret" data-testid="crm-mfa-secret">
                {setupData.secret}
              </code>
            </details>

            <label htmlFor="crm-setup-code">Uygulamadaki 6 haneli kod</label>
            <Input
              id="crm-setup-code"
              data-testid="crm-setup-code"
              required
              inputMode="numeric"
              pattern="[0-9]{6}"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              placeholder="123456"
              value={values.code}
              onChange={e => setValues({ ...values, code: e.target.value.replace(/\D/g, '') })}
            />

            {error && (
              <p role="alert" className="crm-error" data-testid="crm-login-error">
                {error}
              </p>
            )}

            <Button
              className="crm-primary crm-login-submit"
              type="submit"
              disabled={busy || values.code.length !== 6}
              data-testid="crm-setup-submit"
            >
              {busy ? 'Etkinleştiriliyor…' : 'Doğrula ve Etkinleştir'}
              <ArrowRight size={17} />
            </Button>

            <button
              type="button"
              className="crm-mfa-logout"
              onClick={restart}
              style={{ marginTop: '16px' }}
            >
              <ArrowLeft size={14} /> Vazgeç ve geri dön
            </button>
          </form>
        )}

        {/* STEP 2C: RECOVERY CODES PRESENTATION */}
        {step === 'recovery' && (
          <div className="crm-mfa-enroll" data-testid="crm-recovery-screen">
            <p className="crm-mfa-hint">
              İki aşamalı doğrulama başarıyla bağlandı. Telefonunuzu kaybederseniz giriş yapabilmek için bu <strong>tek kullanımlık kurtarma kodlarını</strong> güvenli bir yere kaydedin. <strong>Bir daha gösterilmeyecektir.</strong>
            </p>

            <ul className="crm-mfa-codes" data-testid="crm-mfa-recovery-codes">
              {recoveryCodes.map(c => (
                <li key={c}><code>{c}</code></li>
              ))}
            </ul>

            <div className="crm-mfa-actions">
              <Button type="button" variant="outline" onClick={copyCodes} data-testid="crm-mfa-copy">
                {copied ? <Check size={15} /> : <Copy size={15} />}
                {copied ? 'Kopyalandı' : 'Kopyala'}
              </Button>
              <Button type="button" variant="outline" onClick={downloadCodes} data-testid="crm-mfa-download">
                <Download size={15} /> İndir
              </Button>
            </div>

            <label className="crm-mfa-confirm">
              <input
                type="checkbox"
                checked={savedRecovery}
                onChange={e => setSavedRecovery(e.target.checked)}
                data-testid="crm-mfa-saved"
              />
              Kodlarımı güvenli bir yere kaydettim
            </label>

            <Button
              type="button"
              className="crm-primary crm-login-submit"
              disabled={!savedRecovery}
              onClick={() => navigate(target, { replace: true })}
              data-testid="crm-recovery-done"
            >
              Panele Giriş Yap
              <ArrowRight size={17} />
            </Button>
          </div>
        )}

        <div className="crm-login-security">
          <ShieldCheck size={16} />
          <span>Yetkili personel erişimi</span>
        </div>
      </main>

      <footer className="crm-login-footer">
        BROKER CRM <span>Güvenli çalışma alanı</span>
      </footer>
    </div>
  );
}